import pptxgen from 'pptxgenjs';
import dayjs from 'dayjs';
import { SiteChartsConfig } from '../../components/SiteCharts';
import {
  GLOBAL_LTE_CHART_GROUPS,
  GLOBAL_NR_CHART_GROUPS,
  BATCH_DATA_KEY_MAP,
  NR_BATCH_DATA_KEY_MAP,
} from './globalChartConfigs';
import { LOCAL_STORAGE } from '../../globals/globals';
import { getThemeAwareChartColors } from '../../theme/chartColors';
import { getAdaptiveScaleMin, getObservedKpiMin } from './kpiScale';

/** Strip the leading '#' from a CSS hex color for pptxgenjs. */
const hex = (c: string) => c.replace(/^#/, '');

// ── KPI field definitions ────────────────────────────────────────────────────

const LTE_KPI_FIELDS = [
  { key: 'dataAccessibilityPercent',        label: 'Data Accessibility' },
  { key: 'volteAccessibilityPercent',       label: 'VoLTE Accessibility' },
  { key: 'dataRetainabilityPercent',        label: 'Data Retainability' },
  { key: 'volteRetainabilityPercent',       label: 'VoLTE Retainability' },
  { key: 's1SuccessRatePercent',            label: 'S1 Success Rate' },
  { key: 'macroApHandinPercent',            label: 'Macro AP Hand-in' },
  { key: 'apMacroIntraHandoutPercent',      label: 'AP Macro Intra HO' },
  { key: 'apMacroInterFreqHandoutPercent',  label: 'AP Macro Inter-Freq HO' },
  { key: 'intraEranInterFreqPercent',       label: 'Intra ERAN Inter-Freq' },
  { key: 'intraEranIntraFreqPercent',       label: 'Intra ERAN Intra-Freq' },
  { key: 'x1HoIntraFreqPercent',            label: 'X1 HO Intra-Freq' },
  { key: 'x1HoInterFreqPercent',            label: 'X1 HO Inter-Freq' },
];

const NR_TABLE_KPI_FIELDS = [
  { key: 'endcDataAccessibilityPercent',        label: 'ENDC Data Accessibility' },
  { key: 'endcDataRetainabilityPercent',        label: 'ENDC Data Retainability' },
  { key: 'endcSgNbAdditionSuccessRatePercent',  label: 'SgNB Addition SR' },
  { key: 'endcIntraCuIntraFreqHoSrPercent',     label: 'Intra CU Intra-Freq HO SR' },
];

// ── KPI card layout configs (scale + target per KPI) ────────────────────────

const LTE_KPI_CARDS = [
  { title: 'Data Accessibility',        field: 'dataAccessibilityPercent',       scaleMin: 98.5, target: 99.5 },
  { title: 'VoLTE Accessibility',       field: 'volteAccessibilityPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'Data Retainability',        field: 'dataRetainabilityPercent',       scaleMin: 98.5, target: 99.5 },
  { title: 'VoLTE Retainability',       field: 'volteRetainabilityPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'S1 Success Rate',           field: 's1SuccessRatePercent',           scaleMin: 99.0, target: 99.7 },
  { title: 'Macro AP Hand-in',          field: 'macroApHandinPercent',           scaleMin: 97.0, target: 98.5 },
  { title: 'AP Macro Intra Freq HO',    field: 'apMacroIntraHandoutPercent',     scaleMin: 98.5, target: 99.5 },
  { title: 'AP Macro Inter Freq HO',    field: 'apMacroInterFreqHandoutPercent', scaleMin: 98.5, target: 99.5 },
  { title: 'Intra ERAN Inter Freq',     field: 'intraEranInterFreqPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'Intra ERAN Intra Freq',     field: 'intraEranIntraFreqPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'X1 HO Intra Freq',          field: 'x1HoIntraFreqPercent',           scaleMin: 98.5, target: 99.5 },
  { title: 'X1 HO Inter Freq',          field: 'x1HoInterFreqPercent',           scaleMin: 98.0, target: 99.0 },
];

const NR_KPI_CARDS = [
  { title: 'ENDC Data Accessibility',   field: 'endcDataAccessibilityPercent',       scaleMin: 97.0, target: 98.5 },
  { title: 'ENDC Data Retainability',   field: 'endcDataRetainabilityPercent',       scaleMin: 97.0, target: 98.5 },
  { title: 'ENDC SgNB Addition SR',     field: 'endcSgNbAdditionSuccessRatePercent', scaleMin: 97.0, target: 98.5 },
  { title: 'Intra CU Intra Freq HO SR', field: 'endcIntraCuIntraFreqHoSrPercent',   scaleMin: 97.0, target: 98.5 },
];

// ── Structural slide chrome colors (presentation layout, not chart series) ───
const DARK  = '1E293B';  // title bar / cover background
const WHITE = 'FFFFFF';  // slide background
const SLATE = '94A3B8';  // muted text

// ── Per-slide color bundle resolved from design system at export time ────────
interface SlideColors {
  blue:        string;                  // KPI summary bars
  seriesMap:   Record<string, string>;  // series name → hex
  monthlyTrend: string[];               // month 1/2/3 bar colors
}

// ── Slide helpers ────────────────────────────────────────────────────────────

function addFooter(slide: pptxgen.Slide, generatedLabel: string) {
  slide.addText(generatedLabel, {
    x: 0.3, y: 7.1, w: 5, h: 0.3,
    fontSize: 8, color: SLATE, align: 'left', italic: true,
  });
}

function addTitleBar(slide: pptxgen.Slide, left: string, right: string) {
  slide.addShape('rect' as any, { x: 0, y: 0, w: '100%', h: 0.6, fill: { color: DARK } });
  slide.addText(left,  { x: 0.3, y: 0.05, w: 9,   h: 0.5, fontSize: 14, bold: true, color: WHITE });
  slide.addText(right, { x: 9.3, y: 0.05, w: 3.7, h: 0.5, fontSize: 11, color: SLATE, align: 'right' });
}

function addSectionTitleSlide(pptx: pptxgen, heading: string, generatedLabel: string, subtitle?: string) {
  const slide = pptx.addSlide();
  slide.background = { color: DARK };
  slide.addText(heading, {
    x: 0.5, y: 2.8, w: 12.33, h: 1,
    fontSize: 32, bold: true, color: WHITE, align: 'center',
  });
  if (subtitle) {
    slide.addText(subtitle, {
      x: 0.5, y: 4.0, w: 12.33, h: 0.5,
      fontSize: 14, color: SLATE, align: 'center',
    });
  }
  addFooter(slide, generatedLabel);
}

// ── PPTx helper: draw a KPI card with progress bar + sparkline ─────────────
function addKpiCardToSlide(
  slide: pptxgen.Slide,
  x: number, y: number, w: number, h: number,
  cardTitle: string, value: number, unit: string,
  scaleMin: number, scaleMax: number, target: number,
  delta?: number,
  trendLabels?: string[],
  trendValues?: number[],
) {
  const CARD_BG     = 'F8FAFC';
  const CARD_BORDER = 'E2E8F0';
  const MUTED       = '94A3B8';
  const FG          = '1E293B';
  const observedMin = getObservedKpiMin(value, trendValues);
  const effectiveScaleMin = getAdaptiveScaleMin(observedMin, scaleMin);
  const aboveTarget = value >= target;
  const range       = Math.max(0.0001, scaleMax - effectiveScaleMin);
  const valuePct    = Math.min(1, Math.max(0, (value - effectiveScaleMin) / range));
  const targetPct   = Math.min(1, Math.max(0, (target - effectiveScaleMin) / range));
  const compact     = h <= 1.9;

  slide.addShape('rect' as any, { x, y, w, h, fill: { color: CARD_BG }, line: { color: CARD_BORDER, width: 0.75 } });

  slide.addText(cardTitle.toUpperCase(), {
    x: x + 0.1, y: y + 0.07, w: w - 0.2, h: 0.18,
    fontSize: 6.5, bold: true, color: MUTED, align: 'left',
  });
  slide.addText(`${value.toFixed(2)}${unit}`, {
    x: x + 0.1, y: y + (compact ? 0.2 : 0.24), w: w - 0.2, h: 0.42,
    fontSize: compact ? 15 : 20, bold: true, color: FG, align: 'left',
  });

  const barY = y + (compact ? 0.62 : 0.78);
  const barH = 0.09;
  const barX = x + 0.15;
  const barW = w - 0.3;
  const trackColor = 'E2E8F0';  // muted gray — unfilled portion
  slide.addShape('rect' as any, { x: barX, y: barY, w: barW, h: barH, fill: { color: trackColor }, line: { color: trackColor, width: 0 } });
  if (valuePct > 0) {
    slide.addShape('rect' as any, {
      x: barX, y: barY, w: Math.max(0.01, barW * valuePct), h: barH,
      fill: { color: aboveTarget ? 'c4d6dd' : 'feaa71' },
      line: { color: aboveTarget ? 'c4d6dd' : 'feaa71', width: 0 },
    });
  }
  slide.addShape('rect' as any, {
    x: barX + barW * targetPct - 0.01, y: barY - 0.04, w: 0.02, h: barH + 0.08,
    fill: { color: FG }, line: { color: FG, width: 0 },
  });

  const labelY = barY + barH + (compact ? 0.03 : 0.04);
  slide.addText(`${effectiveScaleMin}${unit}`, { x: barX, y: labelY, w: 0.5, h: 0.13, fontSize: 6, color: MUTED, align: 'left' });
  slide.addText(`${scaleMax}${unit}`, { x: barX + barW - 0.5, y: labelY, w: 0.5, h: 0.13, fontSize: 6, color: MUTED, align: 'right' });
  const tgtLabelX = barX + barW * targetPct;
  slide.addText(`${target} tgt`, {
    x: Math.max(barX, Math.min(barX + barW - 0.6, tgtLabelX - 0.3)),
    y: labelY, w: 0.6, h: 0.13, fontSize: 6, color: MUTED, align: 'center',
  });

  const hasTrend = trendLabels && trendValues && trendValues.length >= 2 && h >= 1.35;
  if (hasTrend) {
    const sparkY = y + (compact ? 0.92 : 1.12);
    const sparkBottomPad = delta !== undefined ? (compact ? 0.16 : 0.22) : (compact ? 0.08 : 0.12);
    const sparkH = Math.max(compact ? 0.24 : 0.4, y + h - sparkBottomPad - sparkY);
    slide.addChart('line' as any, [{ name: 'Trend', labels: trendLabels, values: trendValues }], {
      x: barX, y: sparkY, w: barW, h: sparkH,
      showValue: false,
      valAxisMinVal: effectiveScaleMin,
      valAxisMaxVal: scaleMax,
      chartColors: ['7f3eed'],
      showLegend: false,
      catAxisLabelFontSize: compact ? 5 : 6.5,
      valAxisHidden: true,
      lineDataSymbol: 'circle',
      lineDataSymbolSize: compact ? 2 : 3,
      plotAreaBorderColor: CARD_BG,
    } as any);
  }

  if (delta !== undefined) {
    const deltaColor = delta >= 0 ? '33b891' : 'ffd749';
    const deltaY = hasTrend ? y + h - (compact ? 0.11 : 0.20) : labelY + (compact ? 0.11 : 0.16);
    slide.addText(`Q Delta: ${delta >= 0 ? '+' : ''}${delta.toFixed(2)}${unit}`, {
      x: barX, y: deltaY, w: barW, h: 0.14,
      fontSize: compact ? 6 : 7, color: deltaColor, align: 'right', bold: true,
    });
  }
}

// ── KPI summary slide: card grid per region ──────────────────────────────────
function addKpiSummarySlide(
  pptx: pptxgen,
  regionRow: any,
  kpiCards: { title: string; field: string; scaleMin: number; target: number }[],
  monthLabels: string[],
  monthData: (Record<string, number> | null)[],
  variantLabel: string,
  timeLabel: string,
  generatedLabel: string,
) {
  const slide = pptx.addSlide();
  slide.background = { color: WHITE };
  addTitleBar(slide, `${variantLabel}  \u2014  ${regionRow.region}`, timeLabel);

  const numCols  = 4;
  const numRows  = Math.ceil(kpiCards.length / numCols);
  const GRID_X   = 0.3;
  const GRID_Y   = 0.75;
  const GAP_X    = 0.12;
  const GAP_Y    = 0.12;
  const gridW    = 13.33 - GRID_X * 2;
  const gridH    = 7.5 - GRID_Y - 0.1;
  const cardW    = (gridW - GAP_X * (numCols - 1)) / numCols;
  const rawCardH = (gridH - GAP_Y * (numRows - 1)) / numRows;
  // Reduce vertical bulk by ~30% for exported KPI cards across LTE/NR layouts.
  const cardH    = Math.min(rawCardH * 0.7, 1.75);

  const availableIndices = monthData.reduce<number[]>((acc, d, i) => {
    if (d !== null) acc.push(i);
    return acc;
  }, []);
  const hasTrendData = availableIndices.length >= 2;
  const trendLbls    = hasTrendData ? availableIndices.map(i => monthLabels[i]) : undefined;

  kpiCards.forEach(({ title: cardTitle, field, scaleMin, target }, idx) => {
    const col   = idx % numCols;
    const row   = Math.floor(idx / numCols);
    const cx    = GRID_X + col * (cardW + GAP_X);
    const cy    = GRID_Y + row * (cardH + GAP_Y);
    const value = parseFloat((regionRow[field] ?? 0).toFixed(2));
    const trendVals = hasTrendData ? availableIndices.map(i => monthData[i]?.[field] ?? 0) : undefined;
    const firstData = monthData[availableIndices[0]];
    const lastData  = monthData[availableIndices[availableIndices.length - 1]];
    const delta = hasTrendData && firstData && lastData
      ? parseFloat(((lastData[field] ?? 0) - (firstData[field] ?? 0)).toFixed(2))
      : undefined;
    addKpiCardToSlide(slide, cx, cy, cardW, cardH, cardTitle, value, '%', scaleMin, 100, target, delta, trendLbls, trendVals);
  });

  addFooter(slide, generatedLabel);
}

function addGlobalChartSlide(
  pptx: pptxgen,
  config: SiteChartsConfig,
  rows: any[],
  regionLabel: string,
  timeLabel: string,
  generatedLabel: string,
  colors: SlideColors,
) {
  if (!rows.length) return;

  const slide = pptx.addSlide();
  slide.background = { color: WHITE };
  addTitleBar(slide, `${config.title}  \u2014  ${regionLabel}`, timeLabel);

  const timestamps = rows.map(r => {
    const t = dayjs(r.timestamp);
    // Show time only when it's not midnight (i.e. intra-day granularity)
    return t.hour() === 0 && t.minute() === 0 ? t.format('MM/DD') : t.format('MM/DD HH:mm');
  });

  const buildSeriesData = (series: typeof config.series) =>
    series.map(s => ({
      name: s.name,
      labels: timestamps,
      values: rows.map(r => {
        const v = r[s.dataKey];
        return v != null ? parseFloat(parseFloat(v).toFixed(2)) : 0;
      }),
    }));

  const barSeries  = config.series.filter(s => s.type === 'bar');
  const lineSeries = config.series.filter(s => s.type === 'line');

  // valAxes[0] = primary = left axis; valAxes[1] = secondary = right axis.
  // Mirror the UI: use yAxes[0]/[1] directly for left/right labels.
  // Apply secondaryValAxis on whichever series has yAxisIndex:1 (not hardcoded to line).
  const barYAxisIndex  = barSeries[0]?.yAxisIndex  ?? 0;
  const lineYAxisIndex = lineSeries[0]?.yAxisIndex ?? 1;

  const primaryAxisName        = config.yAxes[0]?.name ?? '';
  const secondaryAxisName      = config.yAxes[1]?.name ?? '';
  const primaryAxisIsPercent   = config.yAxes[0]?.formatter?.includes('%') ?? false;
  const secondaryAxisIsPercent = config.yAxes[1]?.formatter?.includes('%') ?? false;
  // Use fixed 0-100 for SR axes (max===100); auto-scale for drop rates so the line is visible
  const secondaryAxisMax  = config.yAxes[1]?.max;
  const secondaryAxisMin  = config.yAxes[1]?.min;

  const baseOpts: any = {
    x: 0.3, y: 1.1, w: 12.7, h: 5.2,
    barDir: 'col',
    barGrouping: 'clustered',
    showValue: false,
    valGridLine: { style: 'dash', color: 'E2E8F0' },
    showLegend: true,
    legendPos: 'b',
    catAxisLabelFontSize: 8,
    valAxisLabelFontSize: 9,
    legendFontSize: 9,
  };

  // Derive a human-readable format code based on the max value across bar series
  const maxBarValue = (() => {
    const barData = buildSeriesData(barSeries);
    let max = 0;
    for (const s of barData) for (const v of s.values) if (v > max) max = v;
    return max;
  })();
  const countFormatCode =
    maxBarValue >= 1_000_000_000 ? '0.0,,,"B"' :
    maxBarValue >= 1_000_000     ? '0.0,,"M"'  :
    maxBarValue >= 1_000         ? '0.0,"K"'   :
    '#,##0';

  // Build axis descriptor — only include defined values to avoid corrupt XML
  const buildAxisOpts = (name: string, isPercent: boolean, axisMax?: number, axisMin?: number) => {
    const opts: any = { showValAxisTitle: true, valAxisTitle: name, valAxisTitleFontSize: 9 };
    opts.valAxisLabelFormatCode = isPercent ? '0"%"' : countFormatCode;
    if (axisMax !== undefined) opts.valAxisMaxVal = axisMax;
    if (axisMin !== undefined) opts.valAxisMinVal = axisMin;
    return opts;
  };

  if (lineSeries.length > 0 && barSeries.length > 0) {
    slide.addChart(
      [
        {
          type: 'bar' as any,
          data: buildSeriesData(barSeries),
          options: {
            chartColors: barSeries.map(s => colors.seriesMap[s.color] ?? colors.blue),
            barDir: 'col',
            barGrouping: 'clustered',
            ...(barYAxisIndex === 1 ? { secondaryValAxis: true, secondaryCatAxis: true } : {}),
          } as any,
        },
        {
          type: 'line' as any,
          data: buildSeriesData(lineSeries),
          options: {
            chartColors: lineSeries.map(s => colors.seriesMap[s.color] ?? colors.blue),
            ...(lineYAxisIndex === 1 ? { secondaryValAxis: true, secondaryCatAxis: true } : {}),
          } as any,
        },
      ] as any,
      {
        ...baseOpts,
        valAxes: [
          buildAxisOpts(primaryAxisName, primaryAxisIsPercent),
          buildAxisOpts(secondaryAxisName, secondaryAxisIsPercent, secondaryAxisMax, secondaryAxisMin),
        ],
        catAxes: [{ catAxisTitle: '' }, { catAxisHidden: true }],
      },
    );
  } else {
    const singleAxisOpts = buildAxisOpts(primaryAxisName, primaryAxisIsPercent, config.yAxes[0]?.max, config.yAxes[0]?.min);
    slide.addChart('bar' as any, buildSeriesData(config.series), {
      ...baseOpts,
      ...singleAxisOpts,
      chartColors: config.series.map(s => colors.seriesMap[s.color] ?? colors.blue),
    });
  }
  addFooter(slide, generatedLabel);
}

// ── Monthly trend helpers ──────────────────────────────────────────────────

interface MonthSlice {
  label: string;
  shortLabel: string;
  start: string;
  end: string;
}

/**
 * Returns the 3 months of the prior completed quarter as individual monthly
 * sparkline buckets. Oldest first.
 */
function getPriorQuarterMonths(): MonthSlice[] {
  const now = dayjs();
  const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
  const priorQStart = currentQStart.subtract(3, 'month');
  return [0, 1, 2].map((monthOffset) => {
    const start = priorQStart.add(monthOffset, 'month').startOf('month');
    const end   = start.endOf('month');
    return {
      label:      start.format('MMMM YYYY'),
      shortLabel: start.format('MMM'),
      start: start.format('YYYY-MM-DD'),
      end:   end.format('YYYY-MM-DD'),
    };
  });
}

/**
 * Returns 3 bi-monthly (2-month) buckets covering the prior 2 completed quarters.
 * Oldest first.
 */
function getPriorTwoQuartersBiMonthly(): MonthSlice[] {
  const now = dayjs();
  const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
  const twoQStart = currentQStart.subtract(6, 'month');
  return [0, 1, 2].map((bucketIndex) => {
    const start = twoQStart.add(bucketIndex * 2, 'month').startOf('month');
    const end   = start.add(1, 'month').endOf('month');
    return {
      label:      `${start.format('MMM')}–${end.format('MMM YYYY')}`,
      shortLabel: start.format('MMM'),
      start: start.format('YYYY-MM-DD'),
      end:   end.format('YYYY-MM-DD'),
    };
  });
}

/** Returns sparkline periods only for quarter modes; empty for rolling modes. */
function getSparklinePeriods(timeRange: string): MonthSlice[] {
  if (timeRange === 'lastQuarter')     return getPriorQuarterMonths();
  if (timeRange === 'lastTwoQuarters') return getPriorTwoQuartersBiMonthly();
  return [];
}

/** Returns query params for the main KPI fetch (uses start/end dates for quarters). */
function getKpiDateRange(timeRange: string): Record<string, string> {
  const now = dayjs();
  if (timeRange === 'lastQuarter') {
    const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
    const priorQStart = currentQStart.subtract(3, 'month');
    return {
      start_date: priorQStart.format('YYYY-MM-DD'),
      end_date:   currentQStart.subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
  if (timeRange === 'lastTwoQuarters') {
    const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
    const twoQStart = currentQStart.subtract(6, 'month');
    return {
      start_date: twoQStart.format('YYYY-MM-DD'),
      end_date:   currentQStart.subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
  return { time_range: timeRange };
}

function addMonthlyTrendSlide(
  pptx: pptxgen,
  region: string,
  months: MonthSlice[],
  monthData: (Record<string, number> | null)[],
  kpiFields: { key: string; label: string }[],
  variantLabel: string,
  timeLabel: string,
  generatedLabel: string,
  colors: SlideColors,
) {
  const slide = pptx.addSlide();
  slide.background = { color: WHITE };
  addTitleBar(slide, `${variantLabel} Trend  \u2014  ${region}`, timeLabel);

  const chartData = months.map((month, idx) => ({
    name: month.label,
    labels: kpiFields.map(f => f.label),
    values: kpiFields.map(f => monthData[idx]?.[f.key] ?? 0),
  }));

  slide.addChart('bar' as any, chartData, {
    x: 0.3, y: 1.95, w: 12.7, h: 4.2,
    barDir: 'col',
    barGrouping: 'clustered',
    barGapWidthPct: 20,
    showValue: true,
    dataLabelFormatCode: '0.00"%"',
    dataLabelFontSize: 7,
    valAxisMinVal: 0,
    valAxisMaxVal: 100,
    valGridLine: { style: 'dash', color: 'E2E8F0' },
    chartColors: colors.monthlyTrend,
    valAxisLabelFormatCode: '0"%"',
    showLegend: true,
    legendPos: 'b',
    catAxisLabelFontSize: 8,
    valAxisLabelFontSize: 8,
    legendFontSize: 9,
  } as any);
  addFooter(slide, generatedLabel);
}

// ── Public API ───────────────────────────────────────────────────────────────

export interface ExportPagePptxOpts {
  tab: 'LTE' | 'NR';
  regions: string[];
  timeRange: string;
  truncate: string;
}

const TIME_LABELS: Record<string, string> = {
  lastWeek:        'Last 7-days',
  lastMonth:       'Last 30-days',
  lastQuarter:     'Prior Quarter',
  lastTwoQuarters: 'Prior 2 Quarters',
};

async function fetchJson(url: string): Promise<any> {
  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const res = await fetch(url, {
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function exportPagePptx(opts: ExportPagePptxOpts): Promise<void> {
  const { tab, regions, timeRange, truncate } = opts;
  const timeLabel      = TIME_LABELS[timeRange] ?? timeRange;
  const tabLabel       = tab === 'LTE' ? 'LTE External Performance' : 'NR External Performance';
  const generatedLabel = dayjs().format('MMMM D, YYYY');
  const kpiFields    = tab === 'LTE' ? LTE_KPI_FIELDS : NR_TABLE_KPI_FIELDS;
  const kpiCards     = tab === 'LTE' ? LTE_KPI_CARDS  : NR_KPI_CARDS;
  const kpiEndpoint  = tab === 'LTE'
    ? '/v1/charts/api/lte/kpi-summary-by-region'
    : '/v1/charts/api/nr/nr-table-kpi-summary';
  const globalEndpoint = tab === 'LTE'
    ? '/v1/charts/api/lte/global-all-charts'
    : '/v1/charts/api/nr/global-all-charts';
  const chartGroups  = tab === 'LTE' ? GLOBAL_LTE_CHART_GROUPS : GLOBAL_NR_CHART_GROUPS;
  const batchKeyMap  = tab === 'LTE' ? BATCH_DATA_KEY_MAP : NR_BATCH_DATA_KEY_MAP;

  // ── 1. Fetch per-region KPI summaries + global batch + optional sparkline trend ─
  const globalParams = new URLSearchParams({ truncate, time_range: timeRange });
  // Send region_name params only when a subset is selected (same logic as UI)
  regions.forEach(r => globalParams.append('region_name', r));

  const months = getSparklinePeriods(timeRange);
  const kpiDateRange = getKpiDateRange(timeRange);
  // Sparkline trend slide shown only for quarter modes
  const hasSparkline = months.length > 0;

  const [kpiResults, globalBatch, trendResults] = await Promise.all([
    Promise.all(
      regions.map(region =>
        fetchJson(`${kpiEndpoint}?${new URLSearchParams({ ...kpiDateRange, region })}`)
          .then((rows: any[]) => ({ region, kpiRow: rows[0] ?? null }))
          .catch(() => ({ region, kpiRow: null }))
      )
    ),
    fetchJson(`${globalEndpoint}?${globalParams}`).catch(() => ({})),
    hasSparkline ? Promise.all(
      regions.map(region =>
        Promise.all(
          months.map(month =>
            fetchJson(
              `${kpiEndpoint}?${new URLSearchParams({ start_date: month.start, end_date: month.end, region })}`,
            )
              .then((rows: any[]) => {
                if (!rows.length) return null;
                const result: Record<string, number> = {};
                for (const f of kpiFields) {
                  const sum = rows.reduce((acc: number, r: any) => acc + (parseFloat(r[f.key]) || 0), 0);
                  result[f.key] = parseFloat((sum / rows.length).toFixed(2));
                }
                return result;
              })
              .catch(() => null)
          )
        ).then(monthData => ({ region, monthData }))
      )
    ) : Promise.resolve(regions.map(region => ({ region, monthData: [] as (Record<string, number> | null)[] }))),
  ]);

  // ── 2. Build presentation ────────────────────────────────────────────────────
  const pptx = new pptxgen();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.title  = 'AMS - Performance KPI Summary';

  // ── Resolve series colors from design system at export time ─────────────────
  const tc = getThemeAwareChartColors();
  const colors: SlideColors = {
    blue:         hex(tc.chart4),
    monthlyTrend: [hex(tc.chart1), hex(tc.chart3), hex(tc.chart4)],
    seriesMap: {
      success: hex(tc.success),
      warning: hex(tc.warning),
      total:   hex(tc.total),
      error:   hex(tc.error),
      info:    hex(tc.info),
      active:  hex(tc.active),
      pending: hex(tc.pending),
      chart1:  hex(tc.chart1),
      chart2:  hex(tc.chart2),
      chart3:  hex(tc.chart3),
      chart4:  hex(tc.chart4),
      chart5:  hex(tc.chart5),
    },
  };

  // ── Slide 1: Title page ───────────────────────────────────────────────────────
  const cover = pptx.addSlide();
  cover.background = { color: DARK };
  cover.addText('AMS - Performance KPI Summary', {
    x: 0.5, y: 2.0, w: 12.33, h: 1,
    fontSize: 36, bold: true, color: WHITE, align: 'center',
  });
  cover.addText(tabLabel, {
    x: 0.5, y: 3.1, w: 12.33, h: 0.5,
    fontSize: 18, color: SLATE, align: 'center',
  });
  cover.addText(
    `${dayjs().format('MMMM D, YYYY')}  |  Time Range: ${timeLabel}  |  Interval: ${truncate}`,
    { x: 0.5, y: 3.75, w: 12.33, h: 0.5, fontSize: 13, color: SLATE, align: 'center' },
  );
  cover.addText(`Regions: ${regions.join(', ')}`, {
    x: 0.5, y: 4.35, w: 12.33, h: 0.5, fontSize: 12, color: SLATE, align: 'center',
  });

  // ── Section 1: aggregated global chart slides ─────────────────────────────────
  const regionLabel = regions.length === 1 ? regions[0] : `${regions.length} Regions`;
  for (const group of chartGroups) {
    for (const config of group.configs) {
      const batchKey = batchKeyMap[config.csvFilename];
      const rows: any[] = batchKey && globalBatch[batchKey] ? globalBatch[batchKey] : [];
      addGlobalChartSlide(pptx, config, rows, regionLabel, timeLabel, generatedLabel, colors);
    }
  }

  // ── Section title: Region Wise Summary ───────────────────────────────────────
  addSectionTitleSlide(
    pptx,
    'Region Wise Summary',
    generatedLabel,
    `${tab} KPI Summary${hasSparkline ? ' & Trend' : ''} per region  |  ${timeLabel}`,
  );

  // ── Section 2: KPI summary + optional monthly trend, grouped per region ─────
  const kpiLabel   = tab === 'LTE' ? 'LTE KPI Summary' : 'NR KPI Summary';
  const monthLabels = months.map(m => m.shortLabel);
  const trendByRegion = new Map(trendResults.map(r => [r.region, r.monthData]));
  for (const { region, kpiRow } of kpiResults) {
    if (kpiRow) {
      const monthData = trendByRegion.get(region) ?? [];
      addKpiSummarySlide(pptx, kpiRow, kpiCards, monthLabels, monthData, kpiLabel, timeLabel, generatedLabel);
    }
    if (hasSparkline) {
      const monthData = trendByRegion.get(region);
      if (monthData) {
        addMonthlyTrendSlide(pptx, region, months, monthData, kpiFields, kpiLabel, timeLabel, generatedLabel, colors);
      }
    }
  }

  await pptx.writeFile({
    fileName: `${tab.toLowerCase()}_performance_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pptx`,
  });
}
